import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import { Spin, Typography } from 'antd'
import { rautReportCustomInfoCast } from 'features/indicationTriage'
import { IndicationStats } from 'features/indicationTriage/api/indicationTriageApi'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useGeckoLocale } from 'features/locale'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import useSWRImmutable from 'swr/immutable'
import { GeckoMeasurementUnit } from 'types'

import WaterfallPlot from './reportSignalCharts/WaterfallPlot'

const { Text, Title: AntTitle } = Typography

const fileFetcher = async (url: string): Promise<number[][]> => {
  const res = fetch(url)
    .then((resp) => resp.json())
    .catch(() => {})
  return res
}

const formatPath = (path: string | undefined) => {
  if (path) {
    return path.split('.')[0]
  }
  return ''
}

const useIndicationFiles = (indicationId: string | undefined) => {
  const { fileRefs } = useRolodexFileLS(
    useMemo(
      () =>
        indicationId
          ? {
              entry_id: indicationId,
              path: '**', // All files recursively
            }
          : undefined,
      [indicationId]
    )
  )

  const { urls } = useRolodexSignedURLs(fileRefs)

  const allFetch = React.useCallback(
    () =>
      Promise.all(
        urls.map(async (url, index) => ({
          data: await fileFetcher(url),
          filename: formatPath(fileRefs[index].path),
        }))
      ),
    [fileRefs, urls]
  )

  const { data, isLoading } = useSWRImmutable([urls, fileRefs], allFetch)

  const formattedData: Record<string, number[][]> = data
    ? data.reduce((acc: Record<string, number[][]>, current) => {
        acc[current.filename] = current.data
        return acc
      }, {})
    : {}
  return { data: formattedData, isLoading }
}

export const StatsView: FC<IndicationStats> = (indicationStats) => {
  const { formatMeasurement } = useGeckoLocale()

  return (
    <div>
      <GridContainer>
        <Text strong type={'secondary'}>
          Mean
        </Text>
        <Text>
          {formatMeasurement({
            measurement: indicationStats.mean.convertTo(
              GeckoMeasurementUnit.Millimeters
            ),
          })}
        </Text>

        <Text strong type={'secondary'}>
          Minimum
        </Text>
        <Text>
          {formatMeasurement({
            measurement: indicationStats.minimum.convertTo(
              GeckoMeasurementUnit.Millimeters
            ),
          })}
        </Text>
        <Text strong type={'secondary'}>
          Maximum
        </Text>
        <Text>
          {formatMeasurement({
            measurement: indicationStats.maximum.convertTo(
              GeckoMeasurementUnit.Millimeters
            ),
          })}
        </Text>
        <Text strong type={'secondary'}>
          Std Dev
        </Text>
        <Text>
          {formatMeasurement({
            measurement: indicationStats.std_dev.convertTo(
              GeckoMeasurementUnit.Millimeters
            ),
          })}
        </Text>
      </GridContainer>
    </div>
  )
}

export const Title = () => 'Report Plots'
export const Width = '24rem'
export const CardView: FC<IndicationDetailCardProps> = () => {
  const { indication: indicationItem } = useIndicationData()

  const customInfo = rautReportCustomInfoCast(indicationItem?.customInfo)
  const stats = customInfo?.stats

  const { data, isLoading } = useIndicationFiles(indicationItem?.id)

  return (
    <div>
      {stats && (
        <Spin
          indicator={<LoadingOutlined />}
          size='large'
          spinning={isLoading}
          style={{ minHeight: '50vh' }}>
          <ColumnWrapper>
            <div>
              <AntTitle level={3}> B-Scan </AntTitle>
              <WaterfallPlot
                dataValues={data['bscan']}
                minCoord={stats.min_coords[0]}
              />
            </div>
            <div>
              <AntTitle level={3}> D-Scan </AntTitle>
              <WaterfallPlot
                dataValues={data['dscan']}
                minCoord={stats.min_coords[1]}
                isDScan
              />
            </div>
            <div>
              <AntTitle level={3}> Statistics </AntTitle>
              <StatsView {...stats}></StatsView>
            </div>
          </ColumnWrapper>
        </Spin>
      )}
    </div>
  )
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(4, max-content);
  align-items: center;
  gap: 10px 40%;
`
const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column; /* Arrange children vertically */
  gap: 10px; /* Add space between each child (div) */
  padding: 10px; /* Optional: Add some padding inside the container */
`
